import React from "react"
import { useNavigate, useSearchParams } from "react-router"
import studyRouteNames from "src/pages/study/study-route-names"
import { popSearchParam } from "@myvp/shared/src/functions/pop-search-param"
import { eproModuleRouteNames } from "src/pages/epro-module/epro-module.route-names"
import {
  getSponsorsBookmark,
  clearSponsorsBookmark,
} from "src/functions/manage-sponsors-bookmark"
import NavigateToCustomerLibrary from "src/pages/library-manager/components/navigate-to-customer-library"
import { isSafari } from "@myvp/shared/src/functions/is-safari"
import { libraryManagerRouteNames } from "src/pages/library-manager/library-manager.route-names"
import useInitialMount from "@myvp/shared/src/hooks/use-initial-mount"

export type Destination =
  | "study-overview"
  | "studio"
  | "preview"
  | "study-preview"
  | "eproSurveyLibrary"
  | "library-preview"

export default function SponsorsRedirect() {
  const navigate = useNavigate()
  const [s] = useSearchParams()
  // The double useEffects in StrictMode was removing this and then it wasn't redirecting
  const [bookmark] = React.useState(() => getSponsorsBookmark())
  const [isLibrary, setIsLibrary] = React.useState(false)

  const isInitialMount = useInitialMount()

  React.useEffect(() => {
    // Need to run on initial mount only otherwise bookmark gets cleared and redirect fails on subsequent mounts
    if (isInitialMount) {
      let pathname = ""
      let searchParams = new URLSearchParams(s)
      const destination = popSearchParam(
        searchParams,
        "destination"
      ) as Destination
      const vaultId = popSearchParam(searchParams, "vaultId")
      const globalSponsorStudyId = popSearchParam(
        searchParams,
        "globalSponsorStudyId"
      )
      const surveyId = popSearchParam(searchParams, "surveyId")
      const collectionId = popSearchParam(searchParams, "collectionId")
      // these are deleted here but are used in sponsors-dev-tools
      searchParams.delete("vaultdns")
      searchParams.delete("tk")
      searchParams.delete("authDestination")
      const surveyTemplateId = popSearchParam(searchParams, "surveyTemplateId")

      if (destination === "library-preview" && surveyTemplateId) {
        pathname = libraryManagerRouteNames.globalPreview({
          surveyTemplateId,
        })
      }
      // eproSurveyLibrary is sent by ePRO Vault when opening the Library Manager tab
      else if (destination === "eproSurveyLibrary" && !!vaultId) {
        if (isSafari()) {
          navigate(
            {
              pathname: libraryManagerRouteNames.embeddedStaticPage({
                vaultId,
              }),
              search: searchParams.toString(),
            },
            { replace: true }
          )
          return
        }
        setIsLibrary(true)
        return
      } else if (
        // There will only be a bookmark in the case of a 401 redirect
        !!bookmark &&
        // If there is a globalSponsorStudyId in the search params, ensure it matches the one in the bookmark in case
        // the user switched studies during the redirect.
        // There will not be a globalSponsorStudyId in Library Manager since it's not associated with a study
        (bookmark.searchParams.get("globalSponsorStudyId") ===
          globalSponsorStudyId ||
          !globalSponsorStudyId)
      ) {
        clearSponsorsBookmark()
        const bookmarkUrl = new URL(bookmark)
        bookmarkUrl.searchParams.delete("globalSponsorStudyId")
        pathname = bookmarkUrl.pathname
        searchParams = bookmarkUrl.searchParams
      } else if (
        destination &&
        vaultId &&
        globalSponsorStudyId &&
        destination === "study-overview"
      ) {
        pathname = studyRouteNames.studyOverview({
          vaultId,
          globalSponsorStudyId,
        })
      } else if (destination === "studio" && vaultId && globalSponsorStudyId) {
        pathname = eproModuleRouteNames.studio({
          vaultId,
          globalSponsorStudyId,
        })
      } else if (destination === "preview" && surveyId && collectionId) {
        pathname = eproModuleRouteNames.globalEproPreview({
          collectionId,
          surveyId,
        })
      } else if (destination === "study-preview" && collectionId) {
        pathname = eproModuleRouteNames.studyPreview({ collectionId })
      }

      if (pathname) {
        navigate(
          { pathname, search: searchParams.toString() },
          { replace: true }
        )
      } else {
        throw new Error("fallback")
      }
    }
  }, [bookmark, isInitialMount, navigate, s])

  return isLibrary ? <NavigateToCustomerLibrary /> : <></>
}
